import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { faGlobeEurope, faSlash } from '@fortawesome/pro-duotone-svg-icons';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, Subject, combineLatest, iif, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { WebViewerBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/webviewer-behaviour/webviewer-behaviour.state';
import { WebViewerSettingState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/webviewer/webviewer-settings.state';
import { CallTagsSelectors } from 'src/app/store/samwin/call-tags/call-tags.selectors';
import { SamwebWindowState } from 'src/app/store/utility/samweb-window/samweb-window.state';
import { BasicBrickComponent } from '../basic-brick-component';
import { SamwebBrick } from '../samweb-brick.decorator';
import { WebviewerBrickSettingsComponent } from './webviewer-brick-settings/webviewer-brick-settings.component';

@Component({
   selector: 'samweb-webviewer-brick',
   templateUrl: './webviewer-brick.component.html',
   styleUrls: ['./webviewer-brick.component.scss'],
})
@SamwebBrick({
   brickDefinitionId: 'webViewer',
   brickTitleTranslationKey: 'brick.brick_definitions.web_viewer.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.web_viewer.description',
   brickIcon: faGlobeEurope,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.GENERAL,
   settingsComponent: WebviewerBrickSettingsComponent,
   settingStateHandler: WebViewerSettingState,
})
export class WebviewerBrickComponent extends BasicBrickComponent implements OnInit, OnDestroy {
   brickInstanceId: string;
   public globeIcon = faGlobeEurope;
   public slashIcon = faSlash;
   private destroy$ = new Subject();

   public allowAttributes$: Observable<string>;
   public url$: Observable<SafeUrl>;
   public htmlCode$: Observable<string>;
   public isUrl$: Observable<boolean>;
   public isSecure$: Observable<boolean>;

   @Select(SamwebWindowState.isWindowDraggedOrResized)
   public isWindowDraggedOrResized$: Observable<boolean>;

   constructor(private store: Store, private sanitizer: DomSanitizer) {
      super();
   }
   ngOnInit(): void {
      const resolvedTag$ = combineLatest([
         this.store.select(WebViewerBehaviourState.getTemplateUrlForBrickInstanceFn).pipe(map((getTemplateUrl) => getTemplateUrl(this.brickInstanceId))),
         this.store.select(CallTagsSelectors.getReplaceAllTagsInTextForActiveOrRingingCallOrAfterCallWorkFn),
      ]).pipe(
         map(([templateUrl, getReplacedTags]) => {
            if (templateUrl) {
               const isUrl = templateUrl.startsWith('http') || templateUrl === 'about:blank';
               return getReplacedTags(templateUrl, isUrl).text;
            }
         }),
         distinctUntilChanged(),
         filter((templateUrl) => !_.isEmpty(templateUrl))
      );
      this.htmlCode$ = resolvedTag$;
      this.url$ = resolvedTag$.pipe(map((urlString) => (urlString ? this.sanitizer.bypassSecurityTrustResourceUrl(urlString) : null)));
      this.isUrl$ = resolvedTag$.pipe(
         map((resolvedTag) => {
            if (_.isEmpty(resolvedTag) || _.isNil(resolvedTag)) {
               return false;
            }
            resolvedTag = resolvedTag.toLowerCase();
            return resolvedTag === 'about:blank' || resolvedTag.startsWith('http');
         })
      );
      this.allowAttributes$ = this.store.select(WebViewerSettingState.getAllowAttributes).pipe(map((fn) => fn(this.brickInstanceId)));

      this.isSecure$ = this.url$.pipe(
         map((url) => url?.toString()),
         switchMap((urlString) => iif(() => !_.isNil(urlString), of(urlString.slice(39).startsWith('https')), of(false)))
      );
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
